// Server-owned render numbers for the screens that spend money (see lib/renderConfig).
//
// `config` is null until it lands. That null is load-bearing and callers must
// render it as "unknown" rather than substituting a default: the price of an edit
// is the one number a screen must never guess, because guessing it right today
// only means being wrong the day P01 changes it.

import { useCallback, useEffect, useState } from 'react'
import { fetchRenderConfig } from '../lib/renderConfig'
import type { RenderConfig } from '../lib/renderConfig'

export interface UseRenderConfig {
  config: RenderConfig | null
  error: Error | null
  /** Read the price list again. A screen where the price gates a paid action needs a way
   *  out: without one, a single failed first fetch leaves a dead control for the rest of
   *  the visit (paczka N2, finding Z4). */
  reload: () => void
}

export function useRenderConfig(): UseRenderConfig {
  const [config, setConfig] = useState<RenderConfig | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [nonce, setNonce] = useState(0)
  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let alive = true
    setError(null)
    const ctrl = new AbortController()
    fetchRenderConfig(ctrl.signal)
      .then((c) => { if (alive) setConfig(c) })
      .catch((e) => {
        if (!alive || ctrl.signal.aborted) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
    return () => { alive = false; ctrl.abort() }
  }, [nonce])

  return { config, error, reload }
}
